import * as React from 'react';
import propz from 'propz';
import {
  ClubProgrammeBookingClub,
  ClubProgrammeBookingItem,
  ClubProgrammeStudentConflict
} from 'Src/helpers/service/admin/clubProgrammes';
import { BulkActionMenu } from './BulkActionMenu';
import { ClubProgrammeBookingItemCard } from './ClubProgrammeBookingItemCard';
import { fmtTimeRange, sortBoardBookingItems } from '../helpers/clubProgrammeBookingsBoard';

type Props = {
  club: ClubProgrammeBookingClub;
  activeDayLabel: string;
  isPriorityMode: boolean;
  actionKey: string | null;
  activeStudentConflicts: ClubProgrammeStudentConflict[];
  renderItemActionButtons: (item: ClubProgrammeBookingItem) => React.ReactNode;
  onOpenAnswers: (title: string, item: ClubProgrammeBookingItem) => void;
  onOpenConflict: (conflict: ClubProgrammeStudentConflict) => void;
  onOpenClubLists: (clubId: string, clubName: string) => void;
  onRunClubBulkSelectAction: (clubId: string, mode: 'ALL' | 'FIRST_N', restToWaitingList?: boolean) => void;
  onRunClubConfirmAction: (clubId: string) => void;
  allocationActionsLocked: boolean;
};

export const ClubProgrammeBookingClubColumn: React.FC<Props> = ({
  club,
  activeDayLabel,
  isPriorityMode,
  actionKey,
  activeStudentConflicts,
  renderItemActionButtons,
  onOpenAnswers,
  onOpenConflict,
  onOpenClubLists,
  onRunClubBulkSelectAction,
  onRunClubConfirmAction,
  allocationActionsLocked
}) => {
  const clubName = String(propz.get(club, ['clubName'], 'Club'));
  const schedule = propz.get(club, ['schedule'], {});
  const time = fmtTimeRange(schedule);
  const items: ClubProgrammeBookingItem[] = Array.isArray(club.items) ? club.items : [];
  const rawMaxParticipants = typeof club.maxParticipants === 'number' ? Number(club.maxParticipants) : null;
  const placesCount = rawMaxParticipants === null || rawMaxParticipants === 0 ? null : rawMaxParticipants;
  const filledCount = Number(club.filledCount || 0);
  const selectedClubCount = Number(club.selectedClubCount || 0);
  const selectedWaitingListCount = Number(club.selectedWaitingListCount || 0);
  const confirmedWaitingListCount = Number(club.confirmedWaitingListCount || 0);
  const remainingPlaces = typeof club.remainingPlaces === 'number' ? club.remainingPlaces : null;
  const isOverCapacity = placesCount !== null && filledCount > placesCount;
  const overCapacityCount = isOverCapacity ? filledCount - placesCount : 0;
  const pendingRemoveCount = items.filter(it => !!it.uiState?.isPendingRemove).length;
  const hasAnyBoardChanges = selectedClubCount > 0 || selectedWaitingListCount > 0 || pendingRemoveCount > 0;
  const clubId = String(propz.get(club, ['clubId'], '') || '');
  const bulkAllLabel = isPriorityMode ? 'All P1' : 'All booked';
  const bulkFirstNLabel = isPriorityMode ? 'First N P1' : 'First N';
  const bulkFirstNWithWlLabel = isPriorityMode ? 'First N P1 + WL' : 'First N + WL';
  const confirmButtonText = hasAnyBoardChanges ? 'Confirm club allocations' : 'No changes';
  const confirmButtonTitle = hasAnyBoardChanges
    ? 'Apply pending changes for this club'
    : 'There are no pending changes to apply for this club';

  return (
    <div className="pbClubColumn">
      <div className="pbClubColumnInner">
        <div className="cpClubColumnHeader pbClubColumnHeader">
          <div className="pbClubColumnTitleRow">
            <div className="pbClubColumnTitleMain">
              <div className="pbClubColumnName">{clubName}</div>
              <div className="pbClubColumnTime">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <circle cx="12" cy="12" r="8.5" />
                  <path d="M12 7.5V12l3 2" />
                </svg>
                {activeDayLabel} {time ? `- ${time}` : ''}
              </div>
            </div>
            <div className="pbClubColumnActions">
              <span className="pbClubColumnCount">{items.length}</span>
              <button
                type="button"
                className="pbColumnIconButton"
                onClick={() => onOpenClubLists(clubId, clubName)}
                title="View club lists"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01" />
                </svg>
              </button>
              <BulkActionMenu
                label={allocationActionsLocked ? 'Actions locked' : 'Actions'}
                disabled={actionKey !== null || items.length === 0 || allocationActionsLocked}
                items={[
                  {
                    key: `${clubId}-all`,
                    label: actionKey === `BULK_SELECT_CLUB:${clubId}:ALL:NO_WL` ? 'Saving...' : bulkAllLabel,
                    disabled: actionKey !== null || items.length === 0,
                    onClick: () => onRunClubBulkSelectAction(clubId, 'ALL', false)
                  },
                  {
                    key: `${clubId}-first-n`,
                    label: actionKey === `BULK_SELECT_CLUB:${clubId}:FIRST_N:NO_WL` ? 'Saving...' : bulkFirstNLabel,
                    disabled: actionKey !== null || items.length === 0 || placesCount === null,
                    onClick: () => onRunClubBulkSelectAction(clubId, 'FIRST_N', false)
                  },
                  {
                    key: `${clubId}-first-n-wl`,
                    label: actionKey === `BULK_SELECT_CLUB:${clubId}:FIRST_N:WL` ? 'Saving...' : bulkFirstNWithWlLabel,
                    disabled: actionKey !== null || items.length === 0 || placesCount === null,
                    onClick: () => onRunClubBulkSelectAction(clubId, 'FIRST_N', true)
                  }
                ]}
              />
            </div>
          </div>
          {allocationActionsLocked ? (
            <div className="pbLockedInline">This programme is completed. Club allocation actions are locked here.</div>
          ) : null}
          <div className="pbClubStatsPanel">
            <div className="pbClubStatsGrid">
              <span className="pbClubStat">
                <em>Places</em>
                <b>{placesCount === null ? 'Unlimited' : placesCount}</b>
              </span>
              <span className="pbClubStat">
                <em>Filled</em>
                <b>{filledCount}</b>
              </span>
              <span className="pbClubStat">
                <em>Remaining</em>
                <b>{remainingPlaces === null ? 'Unlimited' : remainingPlaces}</b>
              </span>
              <span className="pbClubStat">
                <em>Waiting list</em>
                <b>{confirmedWaitingListCount}</b>
              </span>
            </div>
            <div className="pbClubPendingRow">
              <span className="pbPendingChip">
                Pending club: <b>{selectedClubCount}</b>
              </span>
              <span className="pbPendingChip">
                Pending WL: <b>{selectedWaitingListCount}</b>
              </span>
              {isOverCapacity ? (
                <span className="pbBadge pbBadge--danger">Over capacity by {overCapacityCount}</span>
              ) : null}
            </div>
            {isOverCapacity ? (
              <div className="pbOverCapacityNote">
                This club is over capacity.
                <div>
                  Places: <b>{placesCount}</b>, filled: <b>{filledCount}</b>, over by <b>{overCapacityCount}</b>.
                </div>
              </div>
            ) : null}
          </div>
          <div className="pbClubColumnFooterActions">
            <button
              type="button"
              className="pbColumnFillButton"
              disabled={actionKey !== null || items.length === 0 || allocationActionsLocked}
              onClick={() => onRunClubBulkSelectAction(clubId, 'FIRST_N', true)}
            >
              Fill to capacity
            </button>
            <button
              type="button"
              className="pbColumnConfirmButton"
              disabled={items.length === 0 || actionKey !== null || !hasAnyBoardChanges || allocationActionsLocked}
              onClick={() => onRunClubConfirmAction(clubId)}
              title={
                allocationActionsLocked
                  ? 'Allocation actions are locked because this programme is completed'
                  : confirmButtonTitle
              }
            >
              {actionKey === `CONFIRM_CLUB:${clubId}` ? 'Saving...' : confirmButtonText}
            </button>
          </div>
        </div>
        <div className="pb-col pbClubColumnBody">
          {items.length === 0 ? (
            <div className="pbColumnEmpty">No bookings in selected statuses.</div>
          ) : (
            items
              .slice()
              .sort(sortBoardBookingItems)
              .map(it => {
                const pid = String(it.student?.permissionId || '');
                const studentConflict = activeStudentConflicts.find(c => String(c.permissionId || '') === pid);
                return (
                  <ClubProgrammeBookingItemCard
                    key={String(it.messageId)}
                    item={it}
                    studentConflict={studentConflict || null}
                    renderItemActionButtons={renderItemActionButtons}
                    onOpenAnswers={onOpenAnswers}
                    onOpenConflict={onOpenConflict}
                    allocationActionsLocked={allocationActionsLocked}
                  />
                );
              })
          )}
        </div>
      </div>
    </div>
  );
};

ClubProgrammeBookingClubColumn.displayName = 'ClubProgrammeBookingClubColumn';
